/* =========================================================================
   Croma events feed — shared helpers for every section that reads the POS
   Party public calendar (GET {base}/api/calendar/public?from&to, max 100 days
   per request). Used by croma-events (the hub), croma-op-schedule (organized
   play) and croma-vegas-events.

   Exposes window.CromaEvents. Load it with `defer`; a section's inline script
   calls CromaEvents.ready(fn), which runs fn once this file has executed (and
   immediately on a theme-editor re-render, when it already has).

   The feed is being extended in parallel, so normalize() fills every newer
   field when it is absent: kind "community", region "utah", game / fee /
   capacity / image null, featured false. `recurring` falls back to "has an
   occurrenceDate", since the feed already sets occurrenceDate on expanded
   series instances.
   ========================================================================= */
(function () {
  if (window.CromaEvents && window.CromaEvents.version) return;

  var pad = function (n) { return (n < 10 ? '0' : '') + n; };
  var toStr = function (d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); };
  // Date objects only from local parts, never from an ISO string (that parses as UTC).
  var parse = function (s) { var p = s.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); };
  var addDays = function (s, n) { var d = parse(s); d.setDate(d.getDate() + n); return toStr(d); };

  // The shop's day, not the visitor's: a customer in Tokyo should see Denver's "today".
  var todayStr = function (tz) {
    var parts = {};
    new Intl.DateTimeFormat('en-US', { timeZone: tz || 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    return parts.year + '-' + parts.month + '-' + parts.day;
  };

  var fmtTime = function (hhmm) {
    var p = hhmm.split(':'), h = +p[0], m = +p[1];
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (m ? ':' + pad(m) : '') + (h < 12 ? ' AM' : ' PM');
  };
  var fmtShort = function (hhmm) {
    var p = hhmm.split(':'), h = +p[0], m = +p[1];
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (m ? ':' + pad(m) : '') + (h < 12 ? 'a' : 'p');
  };
  var timeLabel = function (e) {
    if (!e.startTime) return 'All day';
    return fmtTime(e.startTime) + (e.endTime ? ' – ' + fmtTime(e.endTime) : '');
  };
  var dayLabel = function (s, opts) { return parse(s).toLocaleDateString(undefined, opts); };

  // "$10" / "$12.50" / "Free" / "—" (unknown).
  var fee = function (cents) {
    if (cents == null || isNaN(cents)) return '—';
    if (+cents === 0) return 'Free';
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? String(d) : d.toFixed(2));
  };

  // Flat game tones: one hue per game, used as a 1px rule + dot, never a fill.
  var GAMES = {
    pokemon: { label: 'Pokémon', color: '#f2c14e' },
    mtg: { label: 'Magic', color: '#c9a7ff' },
    one_piece: { label: 'One Piece', color: '#ff7a72' },
    lorcana: { label: 'Lorcana', color: '#82cef4' },
    riftbound: { label: 'Riftbound', color: '#2dd4bf' },
    gundam: { label: 'Gundam', color: '#9bb0c5' },
    digimon: { label: 'Digimon', color: '#60a5fa' },
    yugioh: { label: 'Yu-Gi-Oh!', color: '#e8a15c' },
    union_arena: { label: 'Union Arena', color: '#f472b6' },
    other: { label: 'Other', color: '#adbccd' }
  };
  var COLORS = { teal: '#2dd4bf', blue: '#60a5fa', green: '#1ECC5E', amber: '#f2c14e', red: '#f24d51', purple: '#a78bfa', pink: '#f472b6', slate: '#9bb0c5' };

  function normalize(e) {
    var o = {};
    for (var k in e) if (Object.prototype.hasOwnProperty.call(e, k)) o[k] = e[k];
    o.kind = e.kind || 'community';
    o.region = e.region || 'utah';
    o.game = e.game && GAMES[e.game] ? e.game : (e.game ? 'other' : null);
    o.entryFeeCents = typeof e.entryFeeCents === 'number' ? e.entryFeeCents : null;
    o.capacity = typeof e.capacity === 'number' ? e.capacity : null;
    o.imageUrl = e.imageUrl || null;
    o.featured = e.featured === true;
    o.recurring = typeof e.recurring === 'boolean' ? e.recurring : !!e.occurrenceDate;
    o.endDate = e.endDate || e.startDate;
    o.title = e.title || '';
    return o;
  }

  // One request per (base, range), shared across every section on the page.
  var cache = {};
  function fetchRange(api, from, to) {
    var base = (api || 'https://pos.tcgparty.com').replace(/\/+$/, '');
    var key = base + '|' + from + '|' + to;
    if (!cache[key]) {
      cache[key] = fetch(base + '/api/calendar/public?from=' + from + '&to=' + to, { credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (j) { return (j.events || []).map(normalize); })
        .catch(function (err) { delete cache[key]; throw err; });
    }
    return cache[key];
  }

  // date, then timed after all-day, then time, then title
  function compare(a, b) {
    if (a.startDate !== b.startDate) return a.startDate < b.startDate ? -1 : 1;
    if (!a.startTime !== !b.startTime) return a.startTime ? 1 : -1;
    if (a.startTime && b.startTime && a.startTime !== b.startTime) return a.startTime < b.startTime ? -1 : 1;
    return a.title.localeCompare(b.title);
  }

  // "community,organized_play" -> { community: true, organized_play: true }
  function kindSet(list) {
    var set = {};
    String(list || 'community').split(',').forEach(function (k) { k = k.trim().toLowerCase(); if (k) set[k] = true; });
    return set;
  }

  // Google Calendar "add" link: floating local times + ctz, so it lands at shop time.
  function gcalLink(e, tz, location) {
    var compact = function (s) { return s.replace(/-/g, ''); };
    var dates = e.startTime
      ? compact(e.startDate) + 'T' + e.startTime.replace(':', '') + '00/' + compact(e.endDate) + 'T' + (e.endTime || e.startTime).replace(':', '') + '00'
      : compact(e.startDate) + '/' + compact(addDays(e.endDate, 1));
    var params = new URLSearchParams({ action: 'TEMPLATE', text: e.title, dates: dates, ctz: tz || 'America/Denver' });
    if (e.notes) params.set('details', e.notes);
    params.set('location', location || 'Croma TCG');
    return 'https://calendar.google.com/calendar/render?' + params.toString();
  }

  var el = function (tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  // A flat game badge: <span class="croma-game" style="--g:#hex">Magic</span>
  function gameBadge(game) {
    if (!game) return null;
    var g = GAMES[game] || GAMES.other;
    var b = el('span', 'croma-game', g.label);
    b.style.setProperty('--g', g.color);
    return b;
  }

  window.CromaEvents = {
    version: 1,
    pad: pad, toStr: toStr, parse: parse, addDays: addDays, todayStr: todayStr,
    fmtTime: fmtTime, fmtShort: fmtShort, timeLabel: timeLabel, dayLabel: dayLabel,
    fee: fee, GAMES: GAMES, COLORS: COLORS,
    normalize: normalize, fetchRange: fetchRange, compare: compare, kindSet: kindSet,
    gcalLink: gcalLink, el: el, gameBadge: gameBadge,
    ready: function (fn) { fn(window.CromaEvents); }
  };

  // Drain anything queued by sections whose inline script ran before this file.
  // Sections boot with:
  //   (function (b) { window.CromaEvents ? b(window.CromaEvents) : (window.CromaEventsQueue = window.CromaEventsQueue || []).push(b); })(function (CE) { … });
  var queued = window.CromaEventsQueue || [];
  window.CromaEventsQueue = [];
  queued.forEach(function (fn) { fn(window.CromaEvents); });
})();
